import { useMemo, useState } from "react";
import SectionHeading from "@/components/base/SectionHeading";
import { dramas, dramaGenres } from "@/mocks/dramas";

type Drama = (typeof dramas)[number];

function CastRow({ member }: { member: Drama["cast"][number] }) {
  return (
    <li className="flex items-start gap-2.5 py-2 border-b border-background-100 last:border-0">
      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-accent-500 shrink-0"></span>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-foreground-900">
          <span className="font-medium">{member.role}</span>
          <span className="mx-1.5 text-foreground-300">/</span>
          <span className="text-primary-700 font-medium">{member.actor}</span>
        </p>
        <p className="text-xs text-foreground-500 mt-0.5">{member.note}</p>
      </div>
    </li>
  );
}

function DramaCard({ drama }: { drama: Drama }) {
  const [expanded, setExpanded] = useState(false);
  const visibleCast = expanded ? drama.cast : drama.cast.slice(0, 4);
  const hasMore = drama.cast.length > 4;

  return (
    <article className="reveal rounded-2xl border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors">
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-[190px] shrink-0 relative">
          <div className="w-full h-56 md:h-full">
            <img
              src={drama.poster}
              alt={`韩剧《${drama.title}》剧集海报`}
              title={`${drama.title} 演员表 主演阵容`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[11px] font-label font-medium">
            {drama.badge}
          </span>
        </div>

        <div className="flex-1 p-4 md:p-6">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <h3 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
              《{drama.title}》
            </h3>
            <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-label">
              {drama.genre}
            </span>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-600 font-label">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-tv-2-line text-secondary-500"></i>
              {drama.network}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-film-line text-secondary-500"></i>
              {drama.episodes}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line text-secondary-500"></i>
              {drama.year}
            </span>
            <span className="inline-flex items-center gap-1.5 text-accent-700 font-medium">
              <i className="ri-bar-chart-fill"></i>
              收视 {drama.rating}
            </span>
          </div>

          <p className="mt-3 text-sm leading-relaxed text-foreground-600">{drama.synopsis}</p>

          <div className="mt-4">
            <p className="text-xs font-label font-medium text-foreground-500 mb-1.5">
              演员表 · 共 {drama.cast.length} 位主创
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
              {visibleCast.map((member) => (
                <CastRow key={`${drama.id}-${member.role}`} member={member} />
              ))}
            </ul>
          </div>

          {hasMore && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="mt-3 inline-flex items-center gap-1.5 text-xs font-label font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
            >
              {expanded ? "收起主创名单" : "展开全部主创"}
              <i className={expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

export default function CastSection() {
  const [genre, setGenre] = useState("全部");

  const filtered = useMemo(
    () => (genre === "全部" ? dramas : dramas.filter((d) => d.genre === genre)),
    [genre]
  );

  return (
    <section id="cast" className="relative w-full py-14 md:py-20 bg-background-100">
      <div className="w-full px-4 md:px-8">
        <SectionHeading
          eyebrow="核心内容"
          title="热播剧演员表全收录"
          desc="按剧集列出完整演员表：谁演谁、这个角色在剧里是什么定位，一目了然。用下方标签按类型筛选，快速找到你想追的那一类。"
        />

        <div className="reveal flex flex-wrap items-center gap-2 mb-6 md:mb-8">
          {dramaGenres.map((g) => {
            const active = g === genre;
            return (
              <button
                key={g}
                type="button"
                onClick={() => setGenre(g)}
                aria-pressed={active}
                className={`px-4 py-2 rounded-full text-sm font-label transition-colors cursor-pointer whitespace-nowrap border ${
                  active
                    ? "bg-primary-500 border-primary-500 text-background-50"
                    : "bg-background-50 border-background-300 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
                }`}
              >
                {g}
              </button>
            );
          })}
          <span className="ml-auto text-xs text-foreground-500 font-label">
            共 {filtered.length} 部剧集
          </span>
        </div>

        <div className="space-y-5">
          {filtered.map((drama) => (
            <DramaCard key={drama.id} drama={drama} />
          ))}
        </div>

        {filtered.length === 0 && (
          <p className="text-center text-sm text-foreground-500 py-12">该类型下暂无收录剧集。</p>
        )}
      </div>
    </section>
  );
}